import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getAnalyticsConsent, screenForPath, synchronizeAnalyticsConsent, trackEvent } from '../services/analytics';

export default function AnalyticsTracker() {
  const { pathname } = useLocation();
  const { currentUser } = useAuth();
  const previousScreen = useRef(null);
  const [consent, setConsent] = useState(getAnalyticsConsent);

  useEffect(() => {
    const update = () => {
      synchronizeAnalyticsConsent();
      setConsent(getAnalyticsConsent());
    };
    window.addEventListener('convivial:analytics-consent', update);
    window.addEventListener('storage', update);
    return () => {
      window.removeEventListener('convivial:analytics-consent', update);
      window.removeEventListener('storage', update);
    };
  }, []);

  useEffect(() => {
    if (consent !== 'granted') return undefined;
    const screen = screenForPath(pathname);
    const context = { screen_name: screen, auth_state: currentUser ? 'signed_in' : 'guest' };
    trackEvent('page_view', { ...context, previous_screen: previousScreen.current });
    previousScreen.current = screen;
    let visibleSince = document.visibilityState === 'visible' ? performance.now() : null;
    let formStarted = false;
    let formCompleted = false;
    let abandoned = false;
    const fields = new WeakSet();
    const depths = new Set();
    const formScreens = ['profile', 'table_create', 'table_detail', 'feedback', 'table_chat'];

    // Send visible time in small chunks. Hidden/background time is excluded.
    const flushTime = () => {
      if (visibleSince === null) return;
      const duration = Math.round(performance.now() - visibleSince);
      visibleSince = document.visibilityState === 'visible' ? performance.now() : null;
      if (duration > 0) trackEvent('screen_engagement', { ...context, visible_time_msec: duration });
    };
    const visibility = () => {
      if (document.visibilityState === 'hidden') flushTime();
      else if (visibleSince === null) visibleSince = performance.now();
    };
    const click = (event) => {
      const target = event.target.closest?.('[data-analytics-action]');
      if (target) {
        trackEvent('ui_click', { ...context, action: target.dataset.analyticsAction });
        return;
      }
      const control = event.target.closest?.('button,a,select,[role="button"],[role="tab"],input[type="checkbox"]');
      if (!control || control.closest('[aria-label="Preferenze Analytics"]')) return;
      // Generic controls remain measurable without sending their visible text.
      const role = control.getAttribute('role');
      const action = role === 'tab' ? 'tab_other' : control.matches('a') ? 'link_other'
        : control.matches('select') ? 'select_other' : control.matches('input') ? 'checkbox_other' : 'button_other';
      trackEvent('ui_click', { ...context, action });
    };
    const field = (event) => {
      const target = event.target;
      if (!formScreens.includes(screen) || !target.matches?.('input,textarea,select') || target.type === 'password') return;
      formCompleted = false;
      if (!formStarted) {
        formStarted = true;
        trackEvent('form_start', { ...context, form_name: screen });
      }
      if (fields.has(target)) return;
      fields.add(target);
      const type = target.tagName.toLowerCase() === 'input' ? target.type : target.tagName.toLowerCase();
      // No value, label, name, ID, placeholder or surrounding text is read.
      trackEvent('form_field', { ...context, form_name: screen,
        field_type: ['text', 'textarea', 'select', 'checkbox', 'radio', 'number', 'date', 'file'].includes(type) ? type : 'other' });
    };
    const invalid = () => trackEvent('form_error', { ...context, form_name: screen });
    const completed = (event) => {
      if (['profile_saved', 'table_created', 'table_updated', 'feedback_submitted', 'chat_message_sent'].includes(event.detail?.name)) {
        formCompleted = true;
      }
    };
    const abandon = () => {
      if (formStarted && !formCompleted && !abandoned) {
        abandoned = true;
        trackEvent('form_abandon', { ...context, form_name: screen });
      }
    };
    const pagehide = () => { flushTime(); abandon(); };
    const scroll = () => {
      const height = document.documentElement.scrollHeight - window.innerHeight;
      if (height <= 0) return;
      const percent = Math.round(window.scrollY / height * 100);
      [25, 50, 75, 90].forEach((depth) => {
        if (percent >= depth && !depths.has(depth)) {
          depths.add(depth);
          trackEvent('scroll_depth', { ...context, percent_scrolled: depth });
        }
      });
    };
    const interval = window.setInterval(flushTime, 15000);
    document.addEventListener('visibilitychange', visibility);
    document.addEventListener('click', click, true);
    document.addEventListener('change', field, true);
    document.addEventListener('invalid', invalid, true);
    window.addEventListener('scroll', scroll, { passive: true });
    window.addEventListener('pagehide', pagehide);
    window.addEventListener('convivial:analytics-event', completed);
    return () => {
      flushTime();
      abandon();
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', visibility);
      document.removeEventListener('click', click, true);
      document.removeEventListener('change', field, true);
      document.removeEventListener('invalid', invalid, true);
      window.removeEventListener('scroll', scroll);
      window.removeEventListener('pagehide', pagehide);
      window.removeEventListener('convivial:analytics-event', completed);
    };
  }, [pathname, consent, currentUser]);
  return null;
}
